<template>
  <div class="flex flex-col my-3">
    <label for="participationType" class="font-bold mb-1">Split Type:</label>
    <SelectButton
      v-model="participationType"
      :options="participationTypes"
      id="participationType"
      name="participationType"
      class="text-sm"
      @change="participationTypeChanged"
    />
  </div>
  <ag-grid-vue
    theme="legacy"
    :rowData="dataFiltered"
    :rowClassRules="rowClassRules"
    :columnDefs="colDefs"
    :defaultColDef="defaultColDefs"
    :autoSizeStrategy="{ type: 'fitGridWidth' }"
    :singleClickEdit="true"
    :stopEditingWhenCellsLoseFocus="true"
    :suppressDragLeaveHidesColumns="true"
    domLayout="autoHeight"
    class="py-3"
    :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
    @grid-size-changed="resize"
    @cell-editing-stopped="cellEditComplete"
    @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
  ></ag-grid-vue>

  <p v-show="totalPercent !== 100" class="w-full text-center font-bold text-danger-500">**Warning** - Total percent does not add to 100%</p>
  <SubmitOptions class="mt-3" @cancelled="cancel" @submitted="submit"></SubmitOptions>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import type { ProjectEntity } from "@/types/ProjectEntity";
import { currencyFormatter, getCellEditableClass, numberParser, percentParser } from "@/services/Utility";
import type { ProjectSettings } from "@/types/Projects";
import CustomTagRenderer from "../cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import type { GridApi, GridReadyEvent, RowClassParams, ValueParserParams } from "ag-grid-community";

const gridApi = ref<GridApi>();

const emit = defineEmits<{
  closed: [];
}>();

interface Props {
  splitType: string;
}
const props = defineProps<Props>();

const projectSettings = defineModel<ProjectSettings>("projectSettings", { required: true });
const projectEntities = defineModel<ProjectEntity[]>("projectEntities", { required: true });

const participationType = ref(
  props.splitType === "Lender" ? projectSettings.value.participationMethod : projectSettings.value.equityParticipationMethod
);
const lastParticipationType = ref(participationType.value);
const participationTypes = ref(["RANK", "PERCENT"]);

const dataFiltered = ref(
  projectEntities.value
    .filter((source) => source.type.toUpperCase().includes(props.splitType.toUpperCase()))
    .map((source) => {
      return {
        entityId: source.entity.entityId,
        type: source.type,
        companyName: source.entity.companyName,
        totalFundsAdjustment: source.totalFundsAdjustment,
        balance: source.balance,
        participationPercent: source.participationPercent,
        participationRank: source.participationRank,
      };
    })
);

const totalPercent = ref<number>(
  participationType.value === "PERCENT" ? dataFiltered.value.reduce((accumulator, entity) => accumulator + entity.participationPercent, 0) : 100
);

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
});

const defaultColDefs = {
  floatingFilter: false,
  cellDataType: "number",
  enableCellChangeFlash: true,
  wrapHeaderText: true,
  sortable: false,
};

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const colDefs = ref([
  {
    field: "type",
    headerName: "Type",
    cellDataType: "text",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.SOURCE_TYPE,
    },
    width: 100,
  },
  {
    field: "companyName",
    cellDataType: "text",
    headerName: "Company Name",
    headerClass: "grid-align-center",
  },
  {
    field: "totalFundsAdjustment",
    headerName: "Total Funds",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    width: 75,
  },
  {
    field: "balance",
    headerName: "Funds Available",
    headerClass: "grid-align-center",
    valueFormatter: currencyFormatter,
    width: 75,
  },
  {
    field: "participationPercent",
    headerName: "%",
    headerClass: "grid-align-center",
    width: 50,
    editable: true,
    valueParser: (params: ValueParserParams) => percentParser(params, 6),
    cellClass: () => getCellEditableClass() + " text-center",
    hide: participationType.value !== "PERCENT",
  },
  {
    field: "participationRank",
    headerName: "Rank",
    headerClass: "grid-align-center",
    width: 50,
    editable: true,
    valueParser: (params: ValueParserParams) => numberParser(params, 0),
    cellClass: () => getCellEditableClass() + " text-center",
    hide: participationType.value !== "RANK",
  },
]);

const calculateTotalPercent = (): void => {
  totalPercent.value =
    participationType.value === "PERCENT" ? dataFiltered.value.reduce((accumulator, entity) => accumulator + entity.participationPercent, 0) : 100;
};

const cellEditComplete = (): void => {
  if (participationType.value === "PERCENT") {
    let runningTotal = 0;
    let zeroCount = 0;
    let index = 0;
    dataFiltered.value.forEach((entity: any) => {
      if (entity.participationPercent == 0) {
        zeroCount++;
        if (index == dataFiltered.value.length - 1 && zeroCount == 1) {
          const row = gridApi.value?.getDisplayedRowAtIndex(index);
          if (row) row.setDataValue("participationPercent", 100 - runningTotal);
        }
      }
      runningTotal += entity.participationPercent;
      index++;
    });
  }

  calculateTotalPercent();
};

const participationTypeChanged = (): void => {
  if (participationType.value == null) participationType.value = lastParticipationType.value;

  gridApi.value?.setColumnsVisible(["participationRank"], participationType.value === "RANK");
  gridApi.value?.setColumnsVisible(["participationPercent"], participationType.value === "PERCENT");
  resize();

  lastParticipationType.value = participationType.value;

  calculateTotalPercent();
};

const cancel = (): void => {
  emit("closed");
};

const submit = (): void => {
  if (props.splitType === "Lender") {
    projectSettings.value.participationMethod = participationType.value;
  } else {
    projectSettings.value.equityParticipationMethod = participationType.value;
  }

  projectEntities.value.forEach((projectEntity: ProjectEntity) => {
    const tableValue = dataFiltered.value.find((value) => value.entityId === projectEntity.entity.entityId && value.type == projectEntity.type);
    if (tableValue == null) return;
    projectEntity.participationPercent = tableValue.participationPercent;
    projectEntity.participationRank = tableValue.participationRank;
  });
  emit("closed");
};
</script>
